import React, { useMemo, useState } from 'react';
import { Debt, PedidoDeQuitacao } from '../services/api';
import { PaymentMethod } from '../types/PaymentMethod';
import { Investment } from '../types/Investment';
import { Goal } from '../types/Goal';
import { calcularQuitacao } from '../utils/quitacao';
import { mensagemDeErro } from '../utils/mensagemDeErro';
import { mascara, numero } from './ValorDoMes';
import './ValorDoMes.css';
import './QuitarDivida.css';

/**
 * Quitar parte de uma dívida, ou ela toda (16/09).
 *
 * O pedido: "quitei X e agora minha dívida diminuiu pra Y, e aí atualiza o
 * valor a ser pago, e fica salvo no histórico quanto quitou e quanto
 * economizou, e perguntar de onde tirei essa grana".
 *
 * Três perguntas, nessa ordem, e a conta embaixo ANTES de confirmar: ela vê
 * "economizou R$ X" e só então grava. A prévia é `utils/quitacao.ts`, presa à
 * conta do servidor pela tabela compartilhada.
 *
 * Mesma janela de `ValorDoMes` (classes `vdm-*`): no celular o teclado
 * numérico precisa de espaço, e um popover na linha do card não tem.
 */

type TipoDeOrigem = 'saldo' | 'investment' | 'goal';

interface Props {
  debt: Debt;
  metodos: PaymentMethod[];
  investimentos: Investment[];
  reservas: Goal[];
  fmt: (v: number) => string;
  onConfirmar: (pedido: PedidoDeQuitacao) => Promise<void>;
  onFechar: () => void;
  /** Só para teste: a data da conta. */
  hoje?: Date;
}

const QuitarDivida: React.FC<Props> = ({
  debt, metodos, investimentos, reservas, fmt, onConfirmar, onFechar, hoje,
}) => {
  const [valorPago, setValorPago] = useState('');
  const [modo, setModo] = useState<'parcelas' | 'valor'>('parcelas');
  const [parcelas, setParcelas] = useState('');
  const [parcelaNova, setParcelaNova] = useState('');
  const [origem, setOrigem] = useState<TipoDeOrigem>('saldo');
  const [metodo, setMetodo] = useState(metodos[0]?.id ?? '');
  const [investimento, setInvestimento] = useState(investimentos[0]?.id ?? '');
  const [reserva, setReserva] = useState(reservas[0]?.id ?? '');
  const [ocupado, setOcupado] = useState(false);
  const [erro, setErro] = useState('');

  const pedidoDaConta = {
    valorPago: numero(valorPago),
    modo,
    parcelasRestantes: parcelas === '' ? undefined : Number(parcelas),
    valorParcela: parcelaNova === '' ? undefined : numero(parcelaNova),
  };
  const preenchido = valorPago !== '' && (modo === 'parcelas' ? parcelas !== '' : parcelaNova !== '');
  const veredito = useMemo(
    () => calcularQuitacao(debt, hoje ?? new Date(), pedidoDaConta),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [debt, hoje, valorPago, modo, parcelas, parcelaNova],
  );

  const idDaOrigem = origem === 'saldo' ? metodo : origem === 'investment' ? investimento : reserva;
  const podeConfirmar = preenchido && veredito.ok && !!idDaOrigem && !ocupado;

  const confirmar = async () => {
    if (!podeConfirmar) return;
    setOcupado(true);
    setErro('');
    try {
      await onConfirmar({
        valorPago: pedidoDaConta.valorPago,
        modo,
        ...(modo === 'parcelas'
          ? { parcelasRestantes: pedidoDaConta.parcelasRestantes }
          : { valorParcela: pedidoDaConta.valorParcela }),
        origem: origem === 'saldo' ? { tipo: 'saldo', metodo } : { tipo: origem, id: idDaOrigem },
      });
      onFechar();
    } catch (e) {
      // Falhou: a janela fica aberta e DIZ — fechar calada faria parecer gravado.
      setErro(mensagemDeErro(e, 'Não consegui registrar a quitação. Tente de novo.'));
      setOcupado(false);
    }
  };

  const hojeFaltam = debt.remainingInstallments;

  return (
    <div className="vdm-overlay" onClick={onFechar}>
      <div
        className="vdm-janela qtd-janela"
        role="dialog"
        aria-modal="true"
        aria-label={`Quitar ${debt.name}`}
        onClick={e => e.stopPropagation()}
      >
        <h3 className="vdm-titulo">Quitar {debt.name}</h3>
        <p className="vdm-texto">
          Hoje faltam {hojeFaltam} {hojeFaltam === 1 ? 'parcela' : 'parcelas'} de {fmt(debt.installmentValue)}.
        </p>

        <div className="qtd-campo">
          <label htmlFor="qtd-valor">Quanto você pagou</label>
          <div className="vdm-outro-linha">
            <span className="vdm-rs">R$</span>
            <input
              id="qtd-valor" type="text" inputMode="numeric" placeholder="0,00"
              value={valorPago} onChange={e => setValorPago(mascara(e.target.value))}
            />
          </div>
        </div>

        <fieldset className="qtd-campo">
          <legend>Como o banco abateu</legend>
          <div className="qtd-opcoes">
            <label className={`qtd-opcao${modo === 'parcelas' ? ' ativa' : ''}`}>
              <input type="radio" name="qtd-modo" checked={modo === 'parcelas'} onChange={() => setModo('parcelas')} />
              Menos parcelas
            </label>
            <label className={`qtd-opcao${modo === 'valor' ? ' ativa' : ''}`}>
              <input type="radio" name="qtd-modo" checked={modo === 'valor'} onChange={() => setModo('valor')} />
              Parcela menor
            </label>
          </div>
          {modo === 'parcelas' ? (
            <div className="qtd-sub">
              <label htmlFor="qtd-parcelas">Quantas parcelas faltam agora? (0 se quitou tudo)</label>
              <input
                id="qtd-parcelas" type="number" inputMode="numeric" min={0} max={hojeFaltam}
                value={parcelas} onChange={e => setParcelas(e.target.value.replace(/\D/g, ''))}
              />
            </div>
          ) : (
            <div className="qtd-sub">
              <label htmlFor="qtd-parcela-nova">Valor da parcela nova</label>
              <div className="vdm-outro-linha">
                <span className="vdm-rs">R$</span>
                <input
                  id="qtd-parcela-nova" type="text" inputMode="numeric" placeholder="0,00"
                  value={parcelaNova} onChange={e => setParcelaNova(mascara(e.target.value))}
                />
              </div>
            </div>
          )}
        </fieldset>

        <fieldset className="qtd-campo">
          <legend>De onde saiu o dinheiro</legend>
          <div className="qtd-opcoes">
            {([
              ['saldo', 'Saldo'],
              ['investment', 'Investimento'],
              ['goal', 'Reserva'],
            ] as const).map(([tipo, rotulo]) => (
              <label key={tipo} className={`qtd-opcao${origem === tipo ? ' ativa' : ''}`}>
                <input type="radio" name="qtd-origem" checked={origem === tipo} onChange={() => setOrigem(tipo)} />
                {rotulo}
              </label>
            ))}
          </div>
          <div className="qtd-sub">
            {origem === 'saldo' && (
              metodos.length === 0 ? <p className="qtd-nota">Cadastre um método de pagamento em Métodos.</p> : (
                <>
                  <label htmlFor="qtd-metodo">Por qual método</label>
                  <select id="qtd-metodo" value={metodo} onChange={e => setMetodo(e.target.value)}>
                    {metodos.map(m => <option key={m.id} value={m.id}>{m.name}</option>)}
                  </select>
                </>
              )
            )}
            {origem === 'investment' && (
              investimentos.length === 0 ? <p className="qtd-nota">Você não tem investimento cadastrado.</p> : (
                <>
                  <label htmlFor="qtd-investimento">Qual investimento</label>
                  <select id="qtd-investimento" value={investimento} onChange={e => setInvestimento(e.target.value)}>
                    {investimentos.map(i => <option key={i.id} value={i.id}>{i.name} ({fmt(i.amount)})</option>)}
                  </select>
                </>
              )
            )}
            {origem === 'goal' && (
              reservas.length === 0 ? <p className="qtd-nota">Você não tem reserva com dinheiro guardado.</p> : (
                <>
                  <label htmlFor="qtd-reserva">Qual reserva</label>
                  <select id="qtd-reserva" value={reserva} onChange={e => setReserva(e.target.value)}>
                    {reservas.map(g => <option key={g.id} value={g.id}>{g.name} ({fmt(g.savedAmount)})</option>)}
                  </select>
                </>
              )
            )}
            <p className="qtd-nota">
              {origem === 'saldo'
                ? 'Vira uma saída no saldo de hoje.'
                : 'O valor é descontado de lá; o saldo da conta não muda.'}
            </p>
          </div>
        </fieldset>

        {preenchido && (
          veredito.ok ? (
            <div className="qtd-conta" aria-live="polite">
              <p>
                Faltava <strong>{fmt(veredito.resultado.antes.total)}</strong>{' '}
                ({veredito.resultado.antes.parcelasRestantes} × {fmt(veredito.resultado.antes.valorParcela)}).
              </p>
              <p>
                {veredito.resultado.quitada
                  ? <>Agora a dívida fica <strong>quitada</strong>.</>
                  : <>Agora falta <strong>{fmt(veredito.resultado.depois.total)}</strong>{' '}
                      ({veredito.resultado.depois.parcelasRestantes} × {fmt(veredito.resultado.depois.valorParcela)}).</>}
              </p>
              <p className={veredito.resultado.economia >= 0 ? 'qtd-economia' : 'qtd-custo'}>
                {veredito.resultado.economia >= 0
                  ? <>Você paga {fmt(pedidoDaConta.valorPago)} e economiza <strong>{fmt(veredito.resultado.economia)}</strong>.</>
                  : <>Você paga {fmt(pedidoDaConta.valorPago)}, {fmt(-veredito.resultado.economia)} a mais do que as parcelas que deixa de pagar.</>}
              </p>
            </div>
          ) : (
            <p className="vdm-erro" role="alert">{veredito.motivo}</p>
          )
        )}

        {!debt.deductedFromSalary && (
          <p className="qtd-nota">
            {debt.transactionId
              ? 'O lançamento da parcela é ajustado junto.'
              : 'Sem lançamento da parcela ligado a esta dívida: se ela está lançada em Transações, ajuste por lá.'}
          </p>
        )}

        {erro && <p className="vdm-erro" role="alert">{erro}</p>}
        <button
          type="button" className="vdm-btn vdm-btn-principal"
          disabled={!podeConfirmar} aria-busy={ocupado} onClick={confirmar}
        >
          Confirmar quitação
        </button>
        <button type="button" className="vdm-cancelar" onClick={onFechar} disabled={ocupado}>Cancelar</button>
      </div>
    </div>
  );
};

export default QuitarDivida;
